import { Link } from 'react-router-dom'
import { isLoggedIn, isApproved, isAdmin } from '../lib/auth'

const STATS = [
  { value: '1,196', label: 'Exhibitors' },
  { value: '450K', label: 'Sq Ft' },
  { value: '50K+', label: 'Attendees' },
  { value: '140+', label: 'Sessions' },
]

export default function Home() {
  const loggedIn = isLoggedIn()
  const approved = isApproved()

  return (
    <div>
      {/* Full-viewport animated hero */}
      <section className="hero-section relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Animated background layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-brand-900 via-indigo-900 to-purple-900" />
        <div className="hero-grid absolute inset-0 opacity-20" />
        <div className="hero-glow hero-glow-1" />
        <div className="hero-glow hero-glow-2" />
        <div className="hero-glow hero-glow-3" />

        {/* Floating particles */}
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="hero-particle"
            style={{
              left: `${8 + (i * 7.5) % 85}%`,
              top: `${10 + (i * 13) % 75}%`,
              animationDelay: `${i * 0.4}s`,
              animationDuration: `${3 + (i % 3)}s`,
            }}
          />
        ))}

        <div className="relative z-10 text-center text-white px-4 max-w-5xl mx-auto">
          {/* Live badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-5 py-2 mb-8 animate-fade-in">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" />
            </span>
            <span className="text-sm font-medium">LIVE NOW &mdash; June 22-25, 2026</span>
          </div>

          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black mb-6 tracking-tight animate-slide-up">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-blue-200 to-cyan-200">
              AUTOMATE
            </span>
            <br />
            <span className="text-3xl md:text-5xl lg:text-6xl font-bold text-blue-300">
              2026
            </span>
          </h1>

          <p className="text-lg md:text-xl text-blue-200/90 mb-4 animate-fade-in-delay font-medium">
            McCormick Place, Chicago
          </p>
          <p className="text-base md:text-lg text-blue-300/70 mb-10 max-w-2xl mx-auto animate-fade-in-delay-2">
            Your AI-powered conference companion with voice navigation, smart booth routing, live note-taking, and vendor intelligence
          </p>

          {/* Stats ribbon */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10 animate-fade-in-delay-2">
            {STATS.map(({ value, label }) => (
              <div key={label} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl px-4 py-3">
                <div className="text-2xl md:text-3xl font-black text-white">{value}</div>
                <div className="text-xs md:text-sm text-blue-300/70 uppercase tracking-wider">{label}</div>
              </div>
            ))}
          </div>

          {/* CTA buttons */}
          <div className="flex gap-4 justify-center flex-wrap animate-fade-in-delay-3">
            <Link
              to="/tracks"
              className="group bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg shadow-cyan-500/25 transition-all hover:shadow-cyan-400/40 hover:scale-105"
            >
              <span className="flex items-center gap-2">
                🗺️ Navigate Tracks
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
              </span>
            </Link>
            <Link
              to="/companies"
              className="bg-white/10 backdrop-blur-sm hover:bg-white/20 border border-white/20 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all hover:scale-105"
            >
              🔍 Browse 1,196 Exhibitors
            </Link>
            {!loggedIn && (
              <Link
                to="/login"
                className="bg-white/5 hover:bg-white/15 border border-white/10 text-blue-200 px-8 py-4 rounded-xl font-semibold text-lg transition-all hover:scale-105"
              >
                ✨ Sign In for AI Features
              </Link>
            )}
          </div>
        </div>

        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" className="w-full h-16 md:h-24 text-gray-50" preserveAspectRatio="none">
            <path fill="currentColor" d="M0,64 C480,120 960,0 1440,64 L1440,120 L0,120 Z" />
          </svg>
        </div>
      </section>

      {/* Feature cards */}
      <section className="max-w-6xl mx-auto px-4 py-16 bg-gray-50">
        <h2 className="text-3xl font-bold text-center mb-2">Your Show Floor Arsenal</h2>
        <p className="text-gray-500 text-center mb-12">Everything you need to maximize your time at Automate 2026</p>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: '🤖', title: 'AI Companion', desc: 'Ask for directions, booth recommendations, and vendor intel — by voice or text. Your personal show-floor guide.', color: 'from-purple-500/10 to-indigo-500/10', link: '#', badge: 'Voice + Chat' },
            { icon: '🗺️', title: '5 Smart Tracks', desc: '36 curated stops across 5 themed routes. Priority-ranked with time estimates so you hit every must-see.', color: 'from-cyan-500/10 to-blue-500/10', link: '/tracks', badge: 'Navigate' },
            { icon: '🎙️', title: 'Record & Analyze', desc: 'Record vendor conversations, get AI summaries, and auto-generate LinkedIn/X posts from your booth visits.', color: 'from-green-500/10 to-emerald-500/10', link: '/companies', badge: 'AI Analysis' },
            { icon: '❓', title: '59 Smart Questions', desc: 'Expert questions across 5 categories — products, business, technical, industry, and implementation.', color: 'from-orange-500/10 to-amber-500/10', link: '/companies', badge: '5 Categories' },
            { icon: '📝', title: 'Auto-Save Notes', desc: 'Per-vendor note-taking with 1.5s debounced auto-save. Never lose a thought between booths.', color: 'from-pink-500/10 to-rose-500/10', link: '/companies', badge: 'Auto-Save' },
            { icon: '🔒', title: 'Private & Secure', desc: 'Your notes, recordings, and AI chats are private to your account. Admin-gated access with invite codes.', color: 'from-gray-500/10 to-slate-500/10', link: '/profile', badge: 'Encrypted' },
          ].map(({ icon, title, desc, color, link, badge }) => (
            <Link key={title} to={link} className={`group bg-gradient-to-br ${color} border border-gray-200 rounded-2xl p-6 card-hover`}>
              <div className="flex items-start justify-between mb-3">
                <span className="text-4xl">{icon}</span>
                <span className="text-xs font-medium bg-gray-900 text-white px-2.5 py-1 rounded-full">{badge}</span>
              </div>
              <h3 className="font-bold text-lg mb-2 group-hover:text-brand-600 transition-colors">{title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Category grid */}
      <section className="bg-white py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-2">Exhibitor Categories</h2>
          <p className="text-gray-500 text-center mb-8">1,196 exhibitors across 12 industry segments</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Robotics', badge: 'badge-robotics', slug: 'robotics', count: 102 },
              { label: 'AI / ML', badge: 'badge-ai', slug: 'ai-ml', count: 52 },
              { label: 'Machine Vision', badge: 'badge-vision', slug: 'machine-vision', count: 40 },
              { label: 'Motion Control', badge: 'badge-motion', slug: 'motion-control', count: 53 },
              { label: 'Industrial Auto', badge: 'badge-automation', slug: 'industrial-automation', count: 155 },
              { label: 'Software / DT', badge: 'badge-software', slug: 'software-digital-twin', count: 14 },
              { label: 'Humanoid Robots', badge: 'badge-humanoid', slug: 'humanoid-robots', count: 6 },
              { label: 'Sensors / IoT', badge: 'badge-sensors', slug: 'sensors-iot', count: 20 },
              { label: 'Safety', badge: 'badge-safety', slug: 'safety-compliance', count: 5 },
              { label: 'B2B Services', badge: 'badge-b2b', slug: 'b2b-services', count: 62 },
              { label: 'Logistics', badge: 'badge-logistics', slug: 'logistics-warehousing', count: 15 },
              { label: 'Other', badge: 'badge-other', slug: 'other', count: 753 },
            ].map(({ label, badge, slug, count }) => (
              <Link
                key={slug}
                to={`/companies?category=${slug}`}
                className={`badge ${badge} px-4 py-3.5 text-sm justify-between hover:opacity-80 transition-all hover:scale-105`}
              >
                <span>{label}</span>
                <span className="opacity-60 text-xs">{count}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
